import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { DatePicker } from '../../../components/ui/DatePicker';
import { Badge } from '../../../components/ui/Badge';
interface ReceiveVoucher {
  id: string;
  voucherNo: string;
  date: string;
  transactionMode: 'Cash' | 'Bank';
  bankName?: string;
  accountNo?: string;
  partyName?: string;
  amount: number;
  status: 'Draft' | 'Pending' | 'Approved' | 'Rejected';
  createdBy: string;
  createdAt: string;
}
export function ReceiveVoucherList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<ReceiveVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionMode: '',
    status: '',
    search: ''
  });
  const userRole = 'Finance Manager';
  useEffect(() => {
    loadVouchers();
  }, [filters]);
  const loadVouchers = async () => {
    setLoading(true);
    try {
      const mockData: ReceiveVoucher[] = [{
        id: '1',
        voucherNo: 'RV-2024-001',
        date: '2024-01-15',
        transactionMode: 'Bank',
        bankName: 'Premier Bank',
        accountNo: '1234567890',
        partyName: 'ABC Trading Company',
        amount: 50000,
        status: 'Pending',
        createdBy: 'John Doe',
        createdAt: '2024-01-15 10:30 AM'
      }, {
        id: '2',
        voucherNo: 'RV-2024-002',
        date: '2024-01-16',
        transactionMode: 'Cash',
        partyName: 'XYZ Suppliers Ltd',
        amount: 25000,
        status: 'Approved',
        createdBy: 'Jane Smith',
        createdAt: '2024-01-16 02:15 PM'
      }, {
        id: '3',
        voucherNo: 'RV-2024-003',
        date: '2024-01-17',
        transactionMode: 'Bank',
        bankName: 'Dahabshiil Bank',
        accountNo: '9876543210',
        partyName: 'DEF Services Inc',
        amount: 75000,
        status: 'Draft',
        createdBy: 'Ahmed Ali',
        createdAt: '2024-01-17 11:00 AM'
      }];
      setVouchers(mockData);
    } catch (error) {
      console.error('Failed to load vouchers:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this voucher?')) {
      try {
        console.log('Deleting voucher:', id);
        loadVouchers();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const handleApprove = async (id: string) => {
    try {
      console.log('Approving voucher:', id);
      loadVouchers();
    } catch (error: any) {
      alert(error.message);
    }
  };
  const canEdit = (status: string) => {
    return status === 'Draft' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canDelete = (status: string) => {
    return status === 'Draft' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canApprove = () => {
    return userRole === 'Finance Manager';
  };
  const transactionModeOptions = [{
    value: '',
    label: 'All Modes'
  }, {
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const statusOptions = [{
    value: '',
    label: 'All Status'
  }, {
    value: 'Draft',
    label: 'Draft'
  }, {
    value: 'Pending',
    label: 'Pending'
  }, {
    value: 'Approved',
    label: 'Approved'
  }, {
    value: 'Rejected',
    label: 'Rejected'
  }];
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      Draft: 'default',
      Pending: 'warning',
      Approved: 'success',
      Rejected: 'danger'
    };
    return map[status] || 'default';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-2045">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-2046">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-2047">
          <div data-id="element-2048">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2049">
              Receive Voucher List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-2050">
              Manage cash and bank receipts
            </p>
          </div>
          <div className="flex gap-2" data-id="element-2051">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-2052">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-2053" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            {userRole !== 'Auditor' && <Button onClick={() => navigate('/voucher/receive/create')} data-id="element-2054">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-2055" />
                Create Receive Voucher
              </Button>}
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-2056">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-2057">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2058">
              <Input label="Search" placeholder="Voucher No, Party Name..." value={filters.search} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} data-id="element-2059" />

              <Select label="Transaction Mode" options={transactionModeOptions} value={filters.transactionMode} onChange={e => setFilters({
            ...filters,
            transactionMode: e.target.value
          })} data-id="element-2060" />

              <Select label="Status" options={statusOptions} value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value
          })} data-id="element-2061" />

              <DatePicker label="Date From" value={filters.dateFrom} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-2062" />

              <DatePicker label="Date To" value={filters.dateTo} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-2063" />
            </div>
            <div className="flex justify-end" data-id="element-2064">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            dateFrom: '',
            dateTo: '',
            transactionMode: '',
            status: '',
            search: ''
          })} data-id="element-2065">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-2066">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-2067">
              Loading vouchers...
            </div> : <div className="overflow-x-auto" data-id="element-2068">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2069">
                <thead className="bg-gray-50" data-id="element-2070">
                  <tr data-id="element-2071">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2072">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2073">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2074">
                      Transaction Mode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2075">
                      Bank Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2076">
                      Account No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2077">
                      Party Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-2078">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2079">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2080">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-2081">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2082">
                  {vouchers.length === 0 ? <tr data-id="element-2083">
                      <td colSpan={10} className="px-6 py-12 text-center text-gray-500" data-id="element-2084">
                        No vouchers found. Click "Create Receive Voucher" to add
                        one.
                      </td>
                    </tr> : vouchers.map(voucher => <tr key={voucher.id} className="hover:bg-gray-50" data-id="element-2085">
                        <td className="px-6 py-4" data-id="element-2086">
                          <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-2087">
                            {voucher.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2088">
                          {new Date(voucher.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2089">
                          {voucher.transactionMode}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-2090">
                          {voucher.bankName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-2091">
                          {voucher.accountNo || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-2092">
                          {voucher.partyName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-2093">
                          ${voucher.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-2094">
                          <Badge variant={getStatusVariant(voucher.status)} data-id="element-2095">
                            {voucher.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-2096">
                          {voucher.createdBy}
                        </td>
                        <td className="px-6 py-4" data-id="element-2097">
                          <div className="flex items-center justify-center gap-2" data-id="element-2098">
                            <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-2099">
                              <EyeIcon className="w-4 h-4" data-id="element-2100" />
                            </button>
                            {canEdit(voucher.status) && <button onClick={() => navigate(`/voucher/${voucher.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-2101">
                                <EditIcon className="w-4 h-4" data-id="element-2102" />
                              </button>}
                            {canDelete(voucher.status) && <button onClick={() => handleDelete(voucher.id)} className="text-red-600 hover:text-red-800" title="Delete" data-id="element-2103">
                                <TrashIcon className="w-4 h-4" data-id="element-2104" />
                              </button>}
                            {canApprove() && voucher.status === 'Pending' && <button onClick={() => handleApprove(voucher.id)} className="text-green-600 hover:text-green-800" title="Approve" data-id="element-2105">
                                <CheckIcon className="w-4 h-4" data-id="element-2106" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}